<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import emailIntegration from 'dashboard/api/aceleres/emailIntegration';
import { dynamicTimeLocalized } from 'shared/helpers/timeHelper';
import ChannelConnectionCard from './ChannelConnectionCard.vue';

// Aceleres — painel da aba "E-mail". Mesmo resultado do
// formulário de Integrações do Acelerity: conectar o e-mail cria o SMTP de
// envio e a caixa da Caixa de Entrada. Cada conta é de quem a cadastrou: o
// painel só lista as do próprio usuário, gestor inclusive.
const props = defineProps({
  channel: { type: Object, required: true },
});

const emit = defineEmits(['feedback']);
const { accountScopedRoute } = useAccount();

const accounts = ref([]);
const isLoading = ref(true);
const loadFailed = ref(false);
const isConnecting = ref(false);
const isGoogleConnecting = ref(false);
const removingId = ref(null);
const confirmRemoveId = ref(null);
const formError = ref('');
const showForm = ref(false);
const showAdvanced = ref(false);
const form = reactive({
  email: '',
  password: '',
  display_name: '',
  smtp_host: '',
  smtp_port: '',
  smtp_encryption: '',
  imap_host: '',
  imap_port: '',
  imap_encryption: '',
});

const STATUS_VISUALS = {
  connected: { label: 'Recebendo', class: 'bg-n-teal-2 text-n-teal-11' },
  auth_failed: {
    label: 'Falha de autenticação',
    class: 'bg-n-ruby-2 text-n-ruby-11',
  },
  disabled: {
    label: 'Recebimento parado',
    class: 'bg-n-amber-2 text-n-amber-11',
  },
  error: { label: 'Com erro', class: 'bg-n-ruby-2 text-n-ruby-11' },
  pending: { label: 'Conectando', class: 'bg-n-slate-3 text-n-slate-11' },
};

const PROVIDER_LABELS = {
  google: 'Gmail · Google',
  gmail: 'Gmail',
  yahoo: 'Yahoo',
  uol: 'UOL',
  bol: 'BOL',
  terra: 'Terra',
  ig: 'iG',
  globo: 'Globo.com',
  zoho: 'Zoho Mail',
  custom: 'Servidor próprio',
};

const emailDomain = computed(() =>
  (form.email.split('@')[1] || '').trim().toLowerCase()
);
const isGmail = computed(() =>
  ['gmail.com', 'googlemail.com'].includes(emailDomain.value)
);
const isMicrosoft = computed(() =>
  /(^|\.)(outlook|hotmail|live|msn)\.|office365/.test(emailDomain.value)
);

const accountCards = computed(() =>
  accounts.value.map(account => {
    const visual = STATUS_VISUALS[account.status] || STATUS_VISUALS.error;
    const checkedUnix = account.last_checked_at
      ? Math.floor(new Date(account.last_checked_at).getTime() / 1000)
      : null;
    return {
      account,
      title: account.email,
      subtitle: PROVIDER_LABELS[account.provider] || 'E-mail',
      statusLabel: visual.label,
      statusClass: visual.class,
      metaLine: checkedUnix
        ? `verificado ${dynamicTimeLocalized(checkedUnix)}`
        : '',
      notice: account.status === 'connected' ? '' : account.last_error || '',
      muted: account.status !== 'connected',
    };
  })
);

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await emailIntegration.list();
    accounts.value = data.accounts || [];
    loadFailed.value = false;
    if (!accounts.value.length) showForm.value = true;
  } catch {
    loadFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  Object.keys(form).forEach(key => {
    form[key] = '';
  });
  formError.value = '';
  showAdvanced.value = false;
};

const openForm = (email = '') => {
  resetForm();
  form.email = email;
  showForm.value = true;
};

const connect = async () => {
  formError.value = '';
  if (!form.email.trim() || !form.password) {
    formError.value = 'Informe o e-mail e a senha.';
    return;
  }
  if (isMicrosoft.value) {
    formError.value =
      'Contas Outlook, Hotmail e Office 365 ainda não podem ser conectadas.';
    return;
  }

  isConnecting.value = true;
  try {
    const payload = Object.fromEntries(
      Object.entries(form).filter(([, value]) => String(value).trim() !== '')
    );
    const { data } = await emailIntegration.connect(payload);
    accounts.value = data.accounts || accounts.value;
    resetForm();
    showForm.value = false;
    emit('feedback', data.message || 'E-mail conectado.', 'success');
  } catch (error) {
    formError.value =
      error?.response?.data?.message ||
      'Não foi possível conectar o e-mail. Tente novamente.';
  } finally {
    isConnecting.value = false;
  }
};

const connectGoogle = async () => {
  const googleWindow = window.open('about:blank', '_blank');
  if (!googleWindow) {
    formError.value =
      'O navegador bloqueou a nova aba. Permita pop-ups para o Zoraxy e tente novamente.';
    return;
  }

  googleWindow.opener = null;
  isGoogleConnecting.value = true;
  formError.value = '';
  try {
    const { data } = await emailIntegration.googleAuthorizationUrl();
    if (!data.authorization_url) throw new Error('missing_authorization_url');
    googleWindow.location.replace(data.authorization_url);
  } catch (error) {
    googleWindow.close();
    formError.value =
      error?.response?.data?.message ||
      'Não foi possível iniciar a conexão com o Google. Tente novamente.';
  } finally {
    isGoogleConnecting.value = false;
  }
};

const remove = async account => {
  if (confirmRemoveId.value !== account.id) {
    confirmRemoveId.value = account.id;
    return;
  }
  removingId.value = account.id;
  try {
    const { data } = await emailIntegration.remove(account.id);
    accounts.value = data.accounts || [];
    emit('feedback', data.message || 'E-mail removido.', 'success');
  } catch (error) {
    emit(
      'feedback',
      error?.response?.data?.message || 'Não foi possível remover o e-mail.',
      'error'
    );
  } finally {
    removingId.value = null;
    confirmRemoveId.value = null;
  }
};

// Textos do painel. Ficam aqui, e nao soltos no template, pela mesma regra de
// lint (vue/no-bare-strings-in-template) que o restante da central segue.
const TEXT = {
  intro:
    'Conecte seu e-mail para receber as mensagens na Caixa de Entrada do Zoraxy e responder por lá. Só você vê os e-mails desta conta.',
  loading: 'Carregando…',
  loadFailed: 'Não foi possível ler suas contas de e-mail agora.',
  retry: 'Tentar novamente',
  openInbox: 'Abrir Caixa de Entrada',
  updatePassword: 'Atualizar senha',
  reconnectGoogle: 'Reconectar Google',
  remove: 'Remover',
  confirmRemove: 'Confirmar remoção',
  stopReceiving: 'Parar recebimento',
  confirmStop: 'Confirmar pausa',
  connectAnother: 'Conectar outro e-mail',
  emailLabel: 'Seu e-mail',
  emailPlaceholder: 'seu@empresa.com',
  passwordLabel: 'Senha',
  appPasswordLabel: 'Senha de App',
  passwordPlaceholder: 'Senha do e-mail',
  appPasswordPlaceholder: '16 letras da Senha de App',
  gmailHintBefore: 'No Gmail, use uma',
  gmailHintLink: 'Senha de App',
  gmailHintAfter: '(exige verificação em duas etapas), não a senha normal.',
  microsoftBlocked:
    'Contas Outlook, Hotmail e Office 365 ainda não podem ser conectadas: a Microsoft não aceita login por senha.',
  showAdvanced: 'Configurar manualmente',
  hideAdvanced: 'Ocultar configurações',
  senderName: 'Nome do remetente',
  smtpHost: 'Servidor de envio (SMTP)',
  smtpPlaceholder: 'smtp.seudominio.com',
  imapHost: 'Servidor de recebimento (IMAP)',
  imapPlaceholder: 'imap.seudominio.com',
  port: 'Porta',
  encryption: 'Criptografia',
  connect: 'Conectar e-mail',
  connecting: 'Testando envio e recebimento…',
  googleTitle: 'Gmail com login do Google',
  googleDisclosure:
    'Ao continuar, você autoriza a leitura dos e-mails recebidos para exibi-los na Caixa de Entrada do Zoraxy e o envio de respostas pela conta conectada. O Zoraxy não apaga nem altera mensagens no Gmail.',
  connectGoogle: 'Conectar com Google',
  connectingGoogle: 'Abrindo o Google…',
  passwordAlternative: 'Ou conecte com uma Senha de App',
  cancel: 'Cancelar',
};

const ENCRYPTION_OPTIONS = [
  { value: '', label: 'Automático' },
  { value: 'tls', label: 'TLS' },
  { value: 'ssl', label: 'SSL' },
  { value: 'none', label: 'Nenhuma' },
];

const INPUT_CLASS =
  'rounded-xl border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand';

const cancelForm = () => {
  resetForm();
  showForm.value = false;
};

onMounted(() => {
  const googleOutcome = new URLSearchParams(window.location.search).get(
    'google'
  );
  if (googleOutcome === 'success') {
    emit(
      'feedback',
      'Gmail conectado. As mensagens novas aparecerão na Caixa de Entrada.',
      'success'
    );
  } else if (googleOutcome === 'cancelled') {
    emit('feedback', 'A conexão com o Google foi cancelada.', 'error');
  } else if (googleOutcome === 'error') {
    emit(
      'feedback',
      'Não foi possível conectar o Gmail. Confira as permissões e tente novamente.',
      'error'
    );
  }
  load();
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="text-sm text-n-slate-11">{{ TEXT.intro }}</p>

    <p v-if="isLoading" class="text-sm text-n-slate-10">{{ TEXT.loading }}</p>
    <p
      v-else-if="loadFailed"
      class="rounded-xl border border-dashed border-n-weak p-4 text-sm text-n-slate-11"
    >
      {{ TEXT.loadFailed }}
      <button type="button" class="font-semibold text-n-brand" @click="load">
        {{ TEXT.retry }}
      </button>
    </p>

    <div
      v-if="accountCards.length"
      class="grid grid-cols-1 gap-3 sm:grid-cols-2"
    >
      <ChannelConnectionCard
        v-for="card in accountCards"
        :key="card.account.id"
        :icon="props.channel.icon"
        :icon-gradient="props.channel.gradient"
        :title="card.title"
        :subtitle="card.subtitle"
        subtitle-icon="i-lucide-mail"
        :status-label="card.statusLabel"
        :status-class="card.statusClass"
        :meta-line="card.metaLine"
        :notice="card.notice"
        :muted="card.muted"
      >
        <router-link
          v-if="card.account.status === 'connected'"
          :to="accountScopedRoute('inbox_view', {}, { tab: 'emails' })"
        >
          <Button
            slate
            size="sm"
            :label="TEXT.openInbox"
            icon="i-lucide-inbox"
          />
        </router-link>
        <Button
          v-else
          size="sm"
          :label="
            card.account.provider === 'google'
              ? TEXT.reconnectGoogle
              : TEXT.updatePassword
          "
          icon="i-lucide-key-round"
          @click="
            card.account.provider === 'google'
              ? connectGoogle()
              : openForm(card.account.email)
          "
        />
        <Button
          ruby
          faded
          size="sm"
          :label="
            confirmRemoveId === card.account.id
              ? card.account.provider === 'google'
                ? TEXT.confirmStop
                : TEXT.confirmRemove
              : card.account.provider === 'google'
                ? TEXT.stopReceiving
                : TEXT.remove
          "
          icon="i-lucide-trash-2"
          :is-loading="removingId === card.account.id"
          @click="remove(card.account)"
        />
      </ChannelConnectionCard>
    </div>

    <div v-if="!isLoading && !loadFailed && !showForm">
      <Button
        size="sm"
        :label="TEXT.connectAnother"
        icon="i-lucide-plus"
        @click="openForm()"
      />
    </div>

    <form
      v-if="showForm"
      class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-background p-4"
      @submit.prevent="connect"
    >
      <div
        class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
      >
        <div class="flex items-start gap-3">
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm"
          >
            <i class="i-logos-google-icon text-lg" />
          </span>
          <div class="min-w-0">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ TEXT.googleTitle }}
            </p>
            <p class="mt-1 text-xs leading-5 text-n-slate-11">
              {{ TEXT.googleDisclosure }}
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          :label="
            isGoogleConnecting ? TEXT.connectingGoogle : TEXT.connectGoogle
          "
          icon="i-lucide-log-in"
          :is-loading="isGoogleConnecting"
          :disabled="isGoogleConnecting || isConnecting"
          @click="connectGoogle"
        />
      </div>

      <p class="text-center text-xs font-medium text-n-slate-10">
        {{ TEXT.passwordAlternative }}
      </p>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span class="font-medium">{{ TEXT.emailLabel }}</span>
          <input
            v-model="form.email"
            type="email"
            autocomplete="off"
            :placeholder="TEXT.emailPlaceholder"
            :class="INPUT_CLASS"
          />
        </label>
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span class="font-medium">
            {{ isGmail ? TEXT.appPasswordLabel : TEXT.passwordLabel }}
          </span>
          <input
            v-model="form.password"
            type="password"
            autocomplete="new-password"
            :placeholder="
              isGmail ? TEXT.appPasswordPlaceholder : TEXT.passwordPlaceholder
            "
            :class="INPUT_CLASS"
          />
        </label>
      </div>

      <p
        v-if="isGmail"
        class="rounded-xl bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
      >
        {{ TEXT.gmailHintBefore }}
        <a
          href="https://myaccount.google.com/apppasswords"
          target="_blank"
          rel="noopener noreferrer"
          class="font-semibold underline"
        >
          {{ TEXT.gmailHintLink }}
        </a>
        {{ TEXT.gmailHintAfter }}
      </p>
      <p
        v-else-if="isMicrosoft"
        class="rounded-xl bg-n-ruby-2 px-3 py-2 text-xs text-n-ruby-11"
      >
        {{ TEXT.microsoftBlocked }}
      </p>

      <button
        type="button"
        class="self-start text-xs font-semibold text-n-slate-11 hover:text-n-slate-12"
        @click="showAdvanced = !showAdvanced"
      >
        <i class="i-lucide-settings-2 mr-1 align-middle" />
        {{ showAdvanced ? TEXT.hideAdvanced : TEXT.showAdvanced }}
      </button>

      <div v-if="showAdvanced" class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label
          class="flex flex-col gap-1.5 text-xs text-n-slate-11 sm:col-span-3"
        >
          <span class="font-medium">{{ TEXT.senderName }}</span>
          <input v-model="form.display_name" type="text" :class="INPUT_CLASS" />
        </label>
        <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
          <span class="font-medium">{{ TEXT.smtpHost }}</span>
          <input
            v-model="form.smtp_host"
            type="text"
            :placeholder="TEXT.smtpPlaceholder"
            :class="INPUT_CLASS"
          />
        </label>
        <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
          <span class="font-medium">{{ TEXT.port }}</span>
          <input v-model="form.smtp_port" type="number" :class="INPUT_CLASS" />
        </label>
        <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
          <span class="font-medium">{{ TEXT.encryption }}</span>
          <select v-model="form.smtp_encryption" :class="INPUT_CLASS">
            <option
              v-for="option in ENCRYPTION_OPTIONS"
              :key="`smtp-${option.value}`"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
          <span class="font-medium">{{ TEXT.imapHost }}</span>
          <input
            v-model="form.imap_host"
            type="text"
            :placeholder="TEXT.imapPlaceholder"
            :class="INPUT_CLASS"
          />
        </label>
        <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
          <span class="font-medium">{{ TEXT.port }}</span>
          <input v-model="form.imap_port" type="number" :class="INPUT_CLASS" />
        </label>
        <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
          <span class="font-medium">{{ TEXT.encryption }}</span>
          <select v-model="form.imap_encryption" :class="INPUT_CLASS">
            <option
              v-for="option in ENCRYPTION_OPTIONS"
              :key="`imap-${option.value}`"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
      </div>

      <p
        v-if="formError"
        class="rounded-xl bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ formError }}
      </p>

      <div class="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          :label="isConnecting ? TEXT.connecting : TEXT.connect"
          :icon="props.channel.icon"
          :is-loading="isConnecting"
          :disabled="isConnecting || isMicrosoft"
          @click="connect"
        />
        <Button
          v-if="accounts.length"
          slate
          faded
          size="sm"
          :label="TEXT.cancel"
          :disabled="isConnecting"
          @click="cancelForm"
        />
      </div>
    </form>
  </div>
</template>
